import {
  CURRENCY_META,
  addValue,
  categoryInUse,
  fmt,
  hasValue,
  rateOf,
  unitInUse,
  type CurrencyCode,
} from '@k2pos/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppBar } from '@/components/ui/AppBar';
import { AddValueSheet } from '@/components/ui/AddValueSheet';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmSheet } from '@/components/ui/ConfirmSheet';
import { CredentialsSheet } from '@/components/ui/CredentialsSheet';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Field } from '@/components/ui/Field';
import { RateSheet } from '@/components/ui/RateSheet';
import { Mono, Text } from '@/components/ui/Text';
import { IconChevronRight, IconPlus } from '@/components/icons';
import { cx } from '@/lib/cx';
import { toQrDataUrl } from '@/lib/image';
import { useBackHandling, useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { useShopQr } from '@/hooks/useShopQr';
import { useSync } from '@/hooks/useSync';
import { changeCredentials, type CredentialChange } from '@/sync/client';
import { clearCredentials, loadCredentials, type Credentials } from '@/sync/credentials';
import { tick } from '@/sync/live';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Settings.module.css';

type ValueKind = 'unit' | 'category' | 'expense' | null;

/** What the shop sees about its link to the back office. */
const CONNECTION_LO = {
  starting: 'ກຳລັງໂຫຼດ…',
  live: 'ເຊື່ອມຕໍ່ຢູ່',
  offline: 'ຕໍ່ອິນເຕີເນັດບໍ່ໄດ້',
  unauthorised: 'ຍັງບໍ່ໄດ້ເຂົ້າສູ່ລະບົບ',
} as const;

/** How long to wait after the last keystroke before saving. */
const SAVE_AFTER_MS = 600;

/**
 * Types locally and saves once the shopkeeper stops.
 *
 * Binding an input straight to `updateShop` posted once per keystroke, and each
 * post answers with the shop's *whole* state and replaces the store with it.
 * Saving one shop name was eighteen writes, eighteen `Shop.version` bumps
 * waking the other till, and — because the field's value came back out of the
 * store — characters jumping backwards whenever an early answer landed after a
 * later one. On a slow connection at the counter that is unusable.
 *
 * The pending edit is flushed on unmount too, so walking off the screen mid-word
 * saves rather than silently dropping the last few characters.
 */
function useDraft(serverValue: string, commit: (next: string) => void) {
  const [draft, setDraft] = useState(serverValue);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<string | null>(null);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current === null) return;
    const value = pending.current;
    pending.current = null;
    commitRef.current(value);
  }, []);

  // Nothing typed and nothing waiting: follow the server. This is how the first
  // state to arrive fills the field, and how a back-office edit reaches it.
  useEffect(() => {
    if (pending.current === null) setDraft(serverValue);
  }, [serverValue]);

  useEffect(() => flush, [flush]);

  const onChange = useCallback(
    (next: string) => {
      setDraft(next);
      pending.current = next;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_AFTER_MS);
    },
    [flush],
  );

  return [draft, onChange] as const;
}

/** ຕັ້ງຄ່າຮ້ານ — everything the shop owns about itself. */
export function Settings() {
  const goBack = useGoBack('/more');
  const sync = useSync();
  const [credentials, setCredentials] = useState<Credentials | null>(null);

  useEffect(() => {
    void loadCredentials().then((found) => setCredentials(found ?? null));
  }, []);

  /** Drops the app back to the login screen; nothing local is lost because
   *  nothing local is held. */
  const signOut = async () => {
    await clearCredentials();
    await tick();
  };
  const shop = usePos((s) => s.shop);
  const updateShop = usePos((s) => s.updateShop);
  const units = usePos((s) => s.units);
  const categories = usePos((s) => s.categories);
  const expenseCategories = usePos((s) => s.expenseCategories);
  const catalog = usePos((s) => s.catalog);
  const addUnitOption = usePos((s) => s.addUnitOption);
  const addCategory = usePos((s) => s.addCategory);
  const addExpenseCategory = usePos((s) => s.addExpenseCategory);

  const [name, setName] = useDraft(shop.name, (v) => void updateShop({ name: v }));
  const [phone, setPhone] = useDraft(shop.phone ?? '', (v) => void updateShop({ phone: v }));
  const [address, setAddress] = useDraft(shop.address ?? '', (v) =>
    void updateShop({ address: v }),
  );
  const [footer, setFooter] = useDraft(shop.invoiceFooter ?? '', (v) =>
    void updateShop({ invoiceFooter: v }),
  );
  const [bank, setBank] = useDraft(shop.bankNote ?? '', (v) => void updateShop({ bankNote: v }));
  const [reminder, setReminder] = useDraft(String(shop.reminderDays), (v) => {
    const days = Number(v.replace(/\D/g, ''));
    // A blank field means "leave it alone", not "chase everybody from day one",
    // which is what `|| 0` used to make it mean the moment the field was empty
    // for one keystroke.
    if (v.trim() !== '' && Number.isFinite(days)) void updateShop({ reminderDays: days });
  });

  const [rateFor, setRateFor] = useState<Exclude<CurrencyCode, 'LAK'> | null>(null);
  const [adding, setAdding] = useState<ValueKind>(null);
  const [leaving, setLeaving] = useState(false);
  const [changing, setChanging] = useState(false);
  useBackHandling(false, rateFor || adding || leaving || changing ? 1 : 0, () => {
    setRateFor(null);
    setAdding(null);
    setLeaving(false);
    setChanging(false);
  });

  // Its own action rather than one on the store: credentials are not shop data
  // and never belong in the state the server replaces wholesale.
  const commitCredentials = useCallback(
    (change: CredentialChange) => changeCredentials(change),
    [],
  );
  const {
    run: saveCredentialChange,
    busy: savingCredentials,
    error: credentialError,
    dismiss: dismissCredentialError,
  } = useMoneyAction(commitCredentials, (result) => {
    setChanging(false);
    setCredentials((held) => (held ? { ...held, login: result.login } : held));
  });

  return (
    <>
      <AppBar title="ຕັ້ງຄ່າຮ້ານ" onBack={goBack} />

      <div className={`${styles.body} vscroll`}>
        <Section label="ຂໍ້ມູນຮ້ານ">
          <Labelled label="ຊື່ຮ້ານ">
            <Field value={name} onChange={setName} label="ຊື່ຮ້ານ" placeholder="ຮ້ານ ວັດສະດຸ..." />
          </Labelled>
          <Labelled label="ເບີໂທ">
            <Field
              value={phone}
              onChange={setPhone}
              kind="tel"
              label="ເບີໂທຮ້ານ"
              placeholder="020 5512 3480"
            />
          </Labelled>
          <Labelled label="ທີ່ຢູ່">
            <Field
              value={address}
              onChange={setAddress}
              label="ທີ່ຢູ່ຮ້ານ"
              placeholder="ບ້ານ... ເມືອງ..."
            />
          </Labelled>
          <Labelled label="ຕັ້ງເຕືອນທວງໜີ້ (ມື້)">
            <Field
              value={reminder}
              onChange={setReminder}
              kind="kip"
              label="ຈຳນວນມື້ກ່ອນທວງ"
            />
            <Text size={10} color={colors.textFaint}>
              ບັດ “ຄວນທວງມື້ນີ້” ໜ້າຫຼັກ ຈະເລືອກໜີ້ທີ່ຮອດ {shop.reminderDays} ມື້ກ່ອນ
            </Text>
          </Labelled>
        </Section>

        <Section label="ໃບບິນ">
          <Labelled label="ຂໍ້ຄວາມທ້າຍໃບບິນ">
            <Field
              value={footer}
              onChange={setFooter}
              label="ຂໍ້ຄວາມທ້າຍໃບບິນ"
              placeholder="ຂອບໃຈທີ່ໃຊ້ບໍລິການ"
            />
          </Labelled>
          <Labelled label="ເລກບັນຊີທະນາຄານ">
            <Field
              value={bank}
              onChange={setBank}
              label="ເລກບັນຊີທະນາຄານ"
              placeholder="BCEL 010-12-00-123456789 ທ້າວ ກ"
            />
            <Text size={10} color={colors.textFaint}>
              ຈະຂຶ້ນໃນໃບບິນທຸກໃບ ໃຫ້ລູກຄ້າໂອນໄດ້ · ຫວ່າງໄວ້ = ບໍ່ຂຶ້ນ
            </Text>
          </Labelled>
          <Labelled label="QR ໂອນເງິນຂອງຮ້ານ">
            <QrField />
          </Labelled>
          {/* No device code. Bill numbers come off the server under a row lock,
              so two tills cannot mint the same one and there is nothing for a
              per-device prefix to disambiguate. */}
        </Section>

        <Section label="ອັດຕາແລກປ່ຽນ">
          {CURRENCY_META.filter((c) => c.code !== 'LAK').map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => setRateFor(c.code as Exclude<CurrencyCode, 'LAK'>)}
              className={styles.rowBtn}
            >
              <Card className={styles.row}>
                <div className="grow col">
                  <Text weight={700} size={13} align="left">
                    {c.sym} {c.label}
                  </Text>
                  <Text size={10.5} color={colors.textMuted} align="left">
                    ເກັບເປັນກີບສະເໝີ
                  </Text>
                </div>
                <Mono weight={700} size={13}>
                  {fmt(rateOf(c.code, shop.rates))} ₭
                </Mono>
                <IconChevronRight size={16} className={styles.chevron} />
              </Card>
            </button>
          ))}
        </Section>

        <Section label="ໜ່ວຍຂາຍ">
          <ChipList
            values={units.map((u) => `${u.name}${u.step === 0.5 ? ' ·½' : ''}`)}
            lockedHint={(i) => (unitInUse(catalog, units[i]!.name) ? 'ໃຊ້ຢູ່' : undefined)}
            onAdd={() => setAdding('unit')}
          />
        </Section>

        <Section label="ໝວດສິນຄ້າ">
          <ChipList
            values={categories}
            lockedHint={(i) => (categoryInUse(catalog, categories[i]!) ? 'ໃຊ້ຢູ່' : undefined)}
            onAdd={() => setAdding('category')}
          />
        </Section>

        <Section label="ໝວດລາຍຈ່າຍ">
          <ChipList values={expenseCategories} onAdd={() => setAdding('expense')} />
        </Section>

        <Section label="ບັນຊີ">
          <Card className={styles.row}>
            <div className="grow col">
              <Text weight={700} size={13} align="left">
                {credentials?.shopName ?? shop.name}
              </Text>
              <Text size={10.5} color={colors.textMuted} align="left">
                {credentials?.login ? `${credentials.login} · ` : ''}
                {CONNECTION_LO[sync.status]}
              </Text>
            </div>
          </Card>
          <button type="button" onClick={() => setChanging(true)} className={styles.plainBtn}>
            <Text weight={700} size={12.5} color={colors.navy}>
              ປ່ຽນຊື່ຜູ້ໃຊ້ / ລະຫັດຜ່ານ
            </Text>
          </button>
          <button type="button" onClick={() => setLeaving(true)} className={styles.plainBtn}>
            <Text weight={700} size={12.5} color={colors.navy}>
              ອອກຈາກລະບົບ
            </Text>
          </button>
        </Section>

        {/* No diagnostics section. A journal row-count and "running as: web"
            told the shopkeeper nothing they could act on, and a settings screen
            is not the place to park numbers only a developer can read. */}

        <Mono size={10} color={colors.textFaint} align="center" className={styles.brand}>
          K2TECH
        </Mono>
      </div>

      {rateFor ? (
        <RateSheet
          open
          code={rateFor}
          current={rateOf(rateFor, shop.rates)}
          onSave={(rate) => {
            usePos.getState().setRate(rateFor, rate);
            setRateFor(null);
          }}
          onClose={() => setRateFor(null)}
        />
      ) : null}

      <AddValueSheet
        open={adding !== null}
        title={
          adding === 'unit' ? 'ເພີ່ມໜ່ວຍຂາຍ' : adding === 'category' ? 'ເພີ່ມໝວດສິນຄ້າ' : 'ເພີ່ມໝວດລາຍຈ່າຍ'
        }
        placeholder={adding === 'unit' ? 'ເຊັ່ນ ແກັດ' : 'ເຊັ່ນ ສີທາ'}
        askFractional={adding === 'unit'}
        validate={(v) => {
          const list =
            adding === 'unit'
              ? units.map((u) => u.name)
              : adding === 'category'
                ? categories
                : expenseCategories;
          return hasValue(list, v) ? 'ມີຢູ່ແລ້ວ' : null;
        }}
        onAdd={(value, fractional) => {
          if (adding === 'unit') addUnitOption({ name: value, step: fractional ? 0.5 : 1 });
          else if (adding === 'category') addCategory(value);
          else addExpenseCategory(value);
          setAdding(null);
        }}
        onClose={() => setAdding(null)}
      />

      <CredentialsSheet
        open={changing}
        currentLogin={credentials?.login}
        busy={savingCredentials}
        error={credentialError}
        onSubmit={(change) => saveCredentialChange(change)}
        onClose={() => {
          dismissCredentialError();
          setChanging(false);
        }}
      />

      <ConfirmSheet
        open={leaving}
        title="ອອກຈາກລະບົບບໍ?"
        subject={
          <Text weight={800} size={13.5} align="left">
            {credentials?.shopName ?? shop.name}
          </Text>
        }
        // Nothing is lost — the books are on the server — but the next person at
        // this till has to sign in again, and in a shop that is mid-sale that is
        // worth one tap of warning.
        message="ຂໍ້ມູນຮ້ານຢູ່ໃນເຊີບເວີ ບໍ່ເສຍຫຍັງ · ແຕ່ຕ້ອງເຂົ້າສູ່ລະບົບໃໝ່ຈຶ່ງຂາຍຕໍ່ໄດ້"
        confirmLabel="ອອກຈາກລະບົບ"
        onConfirm={() => void signOut()}
        onClose={() => setLeaving(false)}
      />
    </>
  );
}

/**
 * The shop's bank QR: pick a picture, see it, replace or remove it.
 *
 * A picked file goes through `toQrDataUrl` first — a phone camera hands over
 * four megabytes and the server refuses anything past 256KB, so uploading the
 * original would be a minute of waiting followed by a refusal. Downscaled it
 * is tens of kilobytes and scans just as well.
 *
 * Its own component so the file input, the busy flag and the confirm live
 * beside each other rather than adding four more pieces of state to a screen
 * that is otherwise a list of text fields.
 */
function QrField() {
  const qr = useShopQr();
  const setQr = usePos((s) => s.setShopQr);
  const picker = useRef<HTMLInputElement>(null);
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const commit = useCallback((image: string | null) => setQr(image), [setQr]);
  const { run, busy, error, dismiss } = useMoneyAction(commit, () => setRemoving(false));

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setFailed(null);
    try {
      run(await toQrDataUrl(file));
    } catch {
      // A file the browser cannot decode — an HEIC from an iPhone, a PDF the
      // picker let through. Say so rather than posting something unreadable.
      setFailed('ອ່ານຮູບນີ້ບໍ່ໄດ້ · ລອງຮູບ PNG ຫຼື JPG');
    }
  };

  return (
    <>
      <ErrorBanner message={error ?? failed} onDismiss={() => (error ? dismiss() : setFailed(null))} />

      {qr ? (
        <img src={qr} alt="QR ໂອນເງິນຂອງຮ້ານ" className={styles.qrPreview} />
      ) : (
        <div className={styles.qrEmpty}>
          <Text size={11} color={colors.textFaint} align="center">
            ຍັງບໍ່ມີ QR
          </Text>
        </div>
      )}

      <input
        ref={picker}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className={styles.hiddenInput}
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          // Cleared so picking the same file twice still fires a change — the
          // second attempt after a failed one is exactly that.
          e.target.value = '';
        }}
      />

      <div className={styles.qrActions}>
        <Button
          label={busy ? 'ກຳລັງອັບໂຫລດ…' : qr ? 'ປ່ຽນຮູບ' : 'ເລືອກຮູບ QR'}
          variant="outline"
          className="grow"
          disabled={busy}
          onPress={() => picker.current?.click()}
        />
        {qr ? (
          <Button
            label="ເອົາອອກ"
            variant="outline"
            className="grow"
            disabled={busy}
            onPress={() => setRemoving(true)}
          />
        ) : null}
      </div>

      <Text size={10} color={colors.textFaint}>
        ບັນທຶກຮູບ QR ຈາກແອັບທະນາຄານແລ້ວເລືອກມາໃສ່ · ຈະຂຶ້ນໃນໃບບິນທຸກໃບ
      </Text>
      {/* Said plainly, because the shopkeeper will assume otherwise: this is a
          picture, so the customer types the amount themselves. */}
      <Text size={10} color={colors.textFaint}>
        ເປັນຮູບທຳມະດາ · ລູກຄ້າຕ້ອງພິມຈຳນວນເງິນເອງ
      </Text>

      <ConfirmSheet
        open={removing}
        title="ເອົາ QR ອອກບໍ?"
        message="ໃບບິນຕໍ່ໄປຈະບໍ່ມີ QR · ໃສ່ຮູບໃໝ່ພາຍຫຼັງໄດ້"
        confirmLabel="ເອົາອອກ"
        busy={busy}
        onConfirm={() => run(null)}
        onClose={() => setRemoving(false)}
      />
    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <Text weight={800} size={11} color={colors.textMuted}>
        {label}
      </Text>
      {children}
    </section>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <Text weight={700} size={11} color={colors.textMuted}>
        {label}
      </Text>
      {children}
    </div>
  );
}

/**
 * Add-only, by design.
 *
 * Renaming or deleting a value that is in use would orphan cart lines, saved
 * products and journal entries, and the ledger has to stay readable. Values in
 * use are marked so the reason is visible rather than mysterious.
 */
function ChipList({
  values,
  lockedHint,
  onAdd,
}: {
  values: string[];
  lockedHint?: (index: number) => string | undefined;
  onAdd: () => void;
}) {
  return (
    <div className={styles.chips}>
      {values.map((v, i) => {
        const hint = lockedHint?.(i);
        return (
          <span key={v} className={cx(styles.chip, hint && styles.chipLocked)}>
            <Text weight={700} size={11.5} color={colors.textMuted}>
              {v}
            </Text>
            {hint ? (
              <Text size={9} color={colors.textFaint}>
                {hint}
              </Text>
            ) : null}
          </span>
        );
      })}
      <button type="button" onClick={onAdd} className={styles.chipAdd}>
        <IconPlus size={13} />
      </button>
    </div>
  );
}

/** Re-exported so callers can add to lists without importing core directly. */
export { addValue };
